/**
 * Under the Schedule (15/10/2026 — the Danish clock): where the country's
 * day starts on the Danish clock, and the next day the country's distance
 * to Denmark changes, with this container's Danish start as the example
 * ("from 25/10/2026 🇩🇰 05:00 = 🇦🇺 15:00"). A Specified start within two
 * hours of that seam is flagged: after a change it may land on the other
 * side of the country's midnight. Details behind ⓘ.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Globe2 } from 'lucide-react';
import { localDateInTz, localToDk, nextClockChange } from '@trafficpulse/shared';

import { HintDot } from '@/components/ui';

import { minutesToHHMM } from '../../../../../utils/format';
import { useCountryClock } from '../../../../shared/time/country-clock-context';

const HINT =
  "Times you type are Danish and stay put all year. The country's day still turns at its own midnight. A clock change in Denmark or the country moves the local time beside a Danish time.";

const dmy = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const dayWord = (shift: number) => (shift < 0 ? ' the day before' : shift > 0 ? ' the next day' : '');

export const ClockSeamNote: FC = () => {
  const { tz, flag, runDate } = useCountryClock();
  if (!tz) return null;
  const day = { tz, runDate: runDate ?? localDateInTz(new Date(), tz) };
  const seam = localToDk(0, day);
  const change = nextClockChange(tz, day.runDate);
  const later = change ? change.afterMin > change.beforeMin : false;
  const hint = change
    ? `${HINT} From ${dmy(change.runDate)} the local time is an hour ${later ? 'later' : 'earlier'}.`
    : HINT;
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground" data-testid="clock-seam">
      <Globe2 className="h-3 w-3" aria-hidden />
      <span>
        {flag} 00:00 = 🇩🇰 {minutesToHHMM(seam.dkMin)}
        {dayWord(seam.dayShift)}
      </span>
      <HintDot label={hint} />
    </p>
  );
};
